import type { ComponentType } from 'react';
import type { RouteObject } from 'react-router-dom';

/**
 * REGISTRY DE MODULOS
 *
 * Um modulo e uma pasta em `src/modules/<nome>` que exporta um `AppModule`.
 * Ele declara suas rotas, a flag que o liga e os itens de menu que quer
 * publicar. Nada fora do modulo precisa ser editado para adiciona-lo: o
 * roteador monta o app a partir do registry, e as flags decidem o que sobe.
 *
 * E assim que o roadmap do Cap. 37 vira operacao: a Fase 2 ja esta no bundle,
 * desligada por flag, e o administrador acende quando o negocio pedir.
 */

export type AppModule = {
  key: string;
  /** Ordem no menu e na resolucao de rotas conflitantes. */
  order?: number;
  /** Rotas publicas (dentro do layout do portal). */
  routes?: RouteObject[];
  /** Rotas da area logada do anunciante. */
  advertiserRoutes?: RouteObject[];
  /** Rotas do painel administrativo. */
  adminRoutes?: RouteObject[];
  /**
   * Casca que envolve as rotas de admin deste modulo. Fica no modulo, e nao
   * no roteador, porque o painel e um produto com layout proprio — e outro
   * portal do mesmo nucleo pode querer outro.
   */
  adminLayout?: ComponentType;
  /**
   * Flag que habilita o modulo. Ausente = sempre ligado. Com flag desligada,
   * as rotas nao sao montadas — quem digitar a URL cai no 404 padrao.
   */
  requiredFeature?: string;
  /** Executado uma vez na subida: registrar widgets, secoes, filtros. */
  setup?: () => void;
};

const modules = new Map<string, AppModule>();

export function registerModule(module: AppModule): void {
  if (modules.has(module.key)) {
    throw new Error(`Modulo duplicado: "${module.key}".`);
  }
  modules.set(module.key, module);
}

export function getModules(): AppModule[] {
  return [...modules.values()].sort((a, b) => (a.order ?? 100) - (b.order ?? 100));
}

/** Roda os `setup` de todos os modulos registrados. Idempotente. */
let didSetup = false;
export function setupModules(): void {
  if (didSetup) return;
  didSetup = true;
  for (const module of getModules()) module.setup?.();
}

export function enabledModules(isFeatureOn: (key: string) => boolean): AppModule[] {
  return getModules().filter((module) => !module.requiredFeature || isFeatureOn(module.requiredFeature));
}
